import React, {useContext, useEffect, useRef, useState} from 'react';
import Button from "../../../components/button";
import {toast, ToastContainer} from "react-toastify";
import logo from "../../../assets/images/logo-sentry.png";
import userService from "../../../services/user-service";
import {ProjectContext} from "../../../context/project-context";
import {useNavigate} from "react-router-dom";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {AlertMessage} from "../../../utils/alert-message";
import {AUTH_MESSAGE_KEY} from "../../../providers/auth";
import {FaEye, FaEyeSlash} from "react-icons/fa";

const ENTER_COOLDOWN_MS = 3000;

const LoginComponent = ({isPrivate, setIsLoading}) => {
    const {executeRecaptcha} = useGoogleReCaptcha();
    const [userLogin, setUserLogin] = useState("");
    const [password, setPassword] = useState("");
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [remember, setRemember] = useState(false);
    const [isLoginBlocked, setIsLoginBlocked] = useState(false);
    const lastEnterSubmitAt = useRef(0);
    const {login, getFirstPath} = useContext(ProjectContext);
    const navigate = useNavigate();

    useEffect(() => {
        const authenticationMessage = window.sessionStorage.getItem(AUTH_MESSAGE_KEY);
        if (!authenticationMessage) {
            return;
        }

        window.sessionStorage.removeItem(AUTH_MESSAGE_KEY);
        toast.error(authenticationMessage);
    }, []);


    const signIn = async () => {
        if (isLoginBlocked) {
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        formData.append("login", userLogin);
        formData.append("password", password);
        formData.append("remember", remember);

        if (!isPrivate) {
            if (!executeRecaptcha) {
                toast.error("Não foi possível fazer o login.")
                return;
            }

            const token = await executeRecaptcha("login");
            formData.append("token", token);
        }

        userService.login(formData)
            .then(({data}) => {
                const {permissions} = data;
                login(data);
                navigate(getFirstPath(permissions))
            })
            .catch(error => {
                console.log(error)
                const status = error.response?.status;
                const message = error.response?.data?.message;

                if (status === 401 || status === 400) {
                    setIsLoginBlocked(true);
                }

                toast.error(AlertMessage[message] ?? "Não foi possível fazer o login.");
            })
            .finally(() => setIsLoading(false));
    }

    const handleEnterKeyDown = (event) => {
        if (event.key !== "Enter") {
            return;
        }

        const now = Date.now();

        if (now - lastEnterSubmitAt.current < ENTER_COOLDOWN_MS) {
            return;
        }

        lastEnterSubmitAt.current = now;
        signIn();
    }

    const handleUserLoginChange = (event) => {
        setUserLogin(event.target.value);
        setIsLoginBlocked(false);
    }

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
        setIsLoginBlocked(false);
    }

    return (
        <section className="bg-[#ecedf0]">
            <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
                <div className="w-full bg-white rounded-lg shadow md:mt-0 sm:max-w-md xl:p-0">
                    <span className="flex items-center justify-center mt-6 text-2xl font-semibold text-gray-900">
                        <img className={"w-[50px] h-[55px] mr-2"} src={logo} alt=""/>
                        SISTEMA INTERNO {isPrivate ? " - PRIVADO" : ""}
                    </span>
                    <div className="p-4 space-y-4 md:space-y-6 sm:p-8">
                        <div>
                            <label htmlFor="user" className="block mb-2 text-sm font-medium text-gray-900">
                                Usuário
                            </label>
                            <input type="text" name="user" id="user" value={userLogin}
                                   onChange={handleUserLoginChange} onKeyDown={handleEnterKeyDown}
                                   className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                                   required=""/>
                        </div>
                        <div>
                            <label htmlFor="password"
                                   className="block mb-2 text-sm font-medium text-gray-900">Senha</label>
                            <div className="relative">
                                <input type={isPasswordVisible ? "text" : "password"} name="password" id="password"
                                       onKeyDown={handleEnterKeyDown} value={password}
                                       onChange={handlePasswordChange} autoComplete="current-password"
                                       className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 pr-11"
                                       required=""/>
                                <button type="button"
                                        onClick={() => setIsPasswordVisible(currentValue => !currentValue)}
                                        aria-label={isPasswordVisible ? "Ocultar senha" : "Visualizar senha"}
                                        aria-pressed={isPasswordVisible}
                                        title={isPasswordVisible ? "Ocultar senha" : "Visualizar senha"}
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none rounded-r-lg">
                                    {isPasswordVisible ? <FaEyeSlash aria-hidden="true"/> : <FaEye aria-hidden="true"/>}
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input id="remember" aria-describedby="remember" type="checkbox" checked={remember}
                                           onChange={() => setRemember(!remember)}
                                           className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary-300"
                                           required=""/>
                                </div>
                                <div className="ml-3 text-sm">
                                    <label htmlFor="remember" className="text-gray-500">
                                        Lembrar-me
                                    </label>
                                </div>
                            </div>
                            <Button onClick={signIn} target={"primary"} disabled={isLoginBlocked}>
                                Entrar
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer/>
        </section>
    );
};

export default LoginComponent;
